import { useMemo } from 'react'

import { Link } from 'react-router-dom'

import {
  ContentModuleType,
  PostType,
  SpacePostType,
} from '@tribeplatform/gql-client/types'
import { useContentModuleUtils } from '@tribeplatform/react-components/common'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  PostTypePermission,
  useSpacePostTypeSettings,
} from '@tribeplatform/react-components/Space'
import {
  useAuthMember,
  useSpace,
  useSpacePostTypes,
} from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { Badge } from '@tribeplatform/react-ui-kit/Badge'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { Tooltip } from './Tooltip.js'
import { getContentTypeDetails } from './utils/utils.js'

const SPACE_POST_TYPES_LIMIT = 30

interface Props {
  className?: string
  spaceId: string
}

export const AddPostButton = ({ className, spaceId }: Props) => {
  const { data: space } = useSpace({
    variables: {
      id: spaceId,
    },
  })
  const intl = useI18n()
  const { linkToNewContentForm, isPostType } = useContentModuleUtils()
  const { isGuest } = useAuthMember()
  const { data: spacePostTypesData } = useSpacePostTypes({
    variables: {
      limit: SPACE_POST_TYPES_LIMIT,
      spaceId,
    },
  })
  const { mapToPostTypePermissions } = useSpacePostTypeSettings({
    spaceId,
    isPrivate: space?.private,
  })
  const spacePostTypes = useMemo(
    () => simplifyPaginatedResult<SpacePostType>(spacePostTypesData)?.nodes,
    [spacePostTypesData],
  )
  const {
    availableContentTypes,
    availablePostTypes,
    hasLegacyEventCMSEnabled,
  } = useMemo(() => {
    const availablePostTypes = space?.authMemberProps?.availablePostTypes ?? []
    const contentModules = space?.authMemberProps?.availableContentModules ?? []
    const availableContentTypes: (PostType | ContentModuleType)[] = [
      ...contentModules,
      ...availablePostTypes,
    ]
    const hasLegacyEventCMSEnabled = availablePostTypes.some(
      postType =>
        isPostType(postType) &&
        !postType.archived &&
        postType.name.includes('Event'),
    )

    return {
      hasLegacyEventCMSEnabled,
      availableContentTypes,
      availablePostTypes,
    }
  }, [space, isPostType])

  if (isGuest || !space || availableContentTypes.length === 0) {
    return null
  }

  const postTypePermissions = mapToPostTypePermissions(
    availablePostTypes,
    spacePostTypes,
  )

  if (availableContentTypes.length === 1) {
    const [contentType] = availableContentTypes
    const { label, onlyAdminsCanPost } = getContentTypeDetails({
      intl,
      contentType,
      postTypePermissions,
    })

    const linkToForm = linkToNewContentForm({
      contentType,
      spaceRelativeUrl: space?.relativeUrl,
    })

    return (
      <Button variant="primary" as={Link} to={linkToForm} className={className}>
        <div className="flex items-center gap-2">
          {label}
          {onlyAdminsCanPost && (
            <Tooltip
              tooltip={
                <T
                  id="Blocks.SpaceHeader.AddPostDropdown.Button.Tooltip"
                  defaultMessage="This button is only visible to the admins and staff members"
                />
              }
            >
              <SvgIcon name="eye-off" size="sm" />
            </Tooltip>
          )}
        </div>
      </Button>
    )
  }

  /**
   * @todo: check content module permissions once non-admins can create content modules
   */
  const onlyAdminsCanPost = availablePostTypes.every(
    postType => postTypePermissions[postType.id]?.whoCanPost === 'admins',
  )

  return (
    <Dropdown placement="bottom-end">
      <Dropdown.ButtonMinimal
        aria-label={intl.$t({
          defaultMessage: 'Add post',
          id: 'Posts.Add',
        })}
      >
        <Button
          as="span"
          variant="primary"
          trailingIcon={<SvgIcon name="chevron-down" size="lg" />}
          className={className}
        >
          <div className="flex items-center gap-2">
            <T defaultMessage="Add post" id="Posts.Add" />
            {onlyAdminsCanPost && (
              <Tooltip
                tooltip={
                  <T
                    id="Blocks.SpaceHeader.AddPostButton.Hidden.Tooltip"
                    defaultMessage="This button is only visible to the admins and staff members. You can change the CMS models in space settings to allow other members to post."
                  />
                }
              >
                <SvgIcon name="eye-off" size="sm" />
              </Tooltip>
            )}
          </div>
        </Button>
      </Dropdown.ButtonMinimal>
      <Dropdown.Items>
        {availableContentTypes.map(contentType => (
          <AddPostDropdownItem
            key={isPostType(contentType) ? contentType.id : contentType}
            contentType={contentType}
            spaceRelativeUrl={space?.relativeUrl}
            postTypePermissions={postTypePermissions}
            hasLegacyEventCMSEnabled={hasLegacyEventCMSEnabled}
          />
        ))}
      </Dropdown.Items>
    </Dropdown>
  )
}

interface AddPostDropdownItemProps {
  contentType: PostType | ContentModuleType
  spaceRelativeUrl: string
  postTypePermissions: Record<string, PostTypePermission>
  hasLegacyEventCMSEnabled: boolean
}

const AddPostDropdownItem = ({
  contentType,
  spaceRelativeUrl,
  postTypePermissions,
  hasLegacyEventCMSEnabled,
}: AddPostDropdownItemProps) => {
  const intl = useI18n()
  const { label, onlyAdminsCanPost } = getContentTypeDetails({
    intl,
    contentType,
    postTypePermissions,
  })
  const { linkToNewContentForm, isPostType, isContentModuleType } =
    useContentModuleUtils()
  const linkToForm = linkToNewContentForm({
    contentType,
    spaceRelativeUrl,
  })

  const hiddenTooltip = useMemo(() => {
    if (isPostType(contentType)) {
      return (
        <T
          id="Blocks.SpaceHeader.AddPostButton.Option.CMS.Hidden.Tooltip"
          defaultMessage="This option is only visible to the admins and staff members. You can change the CMS models in space settings to allow other members to post."
        />
      )
    }
    if (isContentModuleType(contentType)) {
      return (
        <T
          id="Blocks.SpaceHeader.AddPostButton.Option.ContentModule.Hidden.Tooltip"
          defaultMessage="This option is only visible to the admins and staff members. You can change the content modules in space settings to allow other members to post."
        />
      )
    }
    return null
  }, [contentType, isContentModuleType, isPostType])

  return (
    <Dropdown.Item
      leadingIcon={<SvgIcon name="post" />}
      as={Link}
      to={linkToForm}
    >
      <div className="flex items-center gap-2">
        {label}
        <ContentTypeBadge
          contentType={contentType}
          hasLegacyEventCMSEnabled={hasLegacyEventCMSEnabled}
        />
        {onlyAdminsCanPost && (
          <Tooltip tooltip={hiddenTooltip}>
            <SvgIcon name="eye-off" size="sm" />
          </Tooltip>
        )}
      </div>
    </Dropdown.Item>
  )
}

interface ContentTypeBadgeProps {
  contentType: PostType | ContentModuleType
  hasLegacyEventCMSEnabled: boolean
}

const ContentTypeBadge = ({
  contentType,
  hasLegacyEventCMSEnabled,
}: ContentTypeBadgeProps) => {
  const { isContentModuleType } = useContentModuleUtils()

  if (!isContentModuleType(contentType)) {
    return null
  }

  if (ContentModuleType.event === contentType && hasLegacyEventCMSEnabled) {
    return (
      <Badge
        size="sm"
        variant="positive"
        leadingIcon={<SvgIcon name="space" size="sm" />}
      >
        <T
          id="Composer.ContentTypeSelector.EventModule.BadgeText"
          defaultMessage="Easy events"
        />
      </Badge>
    )
  }

  return null
}
